import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { notification } from 'antd'
import MessageModal from 'components/Modals/MessageModal'
import { RiseUpErrors } from 'constants/errors'
import useErrorMessage from 'hooks/useErrorMessage'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import _ from 'lodash'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { useLocation, useNavigate } from 'react-router-dom'
import swal from 'sweetalert'
import { consoleLog } from 'utils/simpleLogger'
import CompanyLogo from '../../assets/afbc.png'
import Side5 from '../../assets/investing-signup-images/New Pic 1 OP[37].png'
import Button from '../../components/Button'
import ErrorMessage from '../../components/ErrorMessage'
import FormLabel from '../../components/FormLabel'
import InputField from '../../components/InputField'
import LoaderModal from '../../components/Modals/LoaderModal'
import Stepper from '../../components/Stepper'
import { useRiseUpAPIV1 } from '../../hooks/useRiseUpAPIV1'
import { IFormProps, InvestingPersonEnum, RetirementAccountEnum } from '../../interfaces'
import { formatNumber, getModifiedUrlWithStep, isAmtDivisibleBySharePrice } from '../../utils'

const InvestmentAmount = (props: IFormProps) => {
  const sharePrice = Number(process.env.REACT_APP_SHARE_PRICE)
  const minInvestment = Number(process.env.REACT_APP_MIN_INVESTMENT)
  const maxInvestment = Number(process.env.REACT_APP_MAX_INVESTMENT)

  const [isLoading, setIsLoading] = useState(false)
  const { updateOnboardProgress } = useRiseUpAPIV1()
  const sendDataToGTM = useGTMDispatch()
  const { errorMessage, setErrorMessage } = useErrorMessage()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()
  const navigate = useNavigate()
  const location = useLocation()

  const validatePage = async (): Promise<boolean> => {
    const fields = ['investingProfile.amount']
    await props.validateFields(fields)

    return !_.isUndefined(props.errors.investingProfile?.amount)
  }

  const isSubmitDisabled = () => {
    const formValues = props.getValues('investingProfile.amount')
    if (parseFloat(formValues) <= 0 || formValues === '') {
      return true
    }
    return false
  }

  const handleSubmit = async () => {
    if (
      (await validatePage()) ||
      Number(props.getValues('investingProfile.amount')) < minInvestment ||
      Number(props.getValues('investingProfile.amount')) > maxInvestment ||
      !isAmtDivisibleBySharePrice(props.getValues('investingProfile.amount'), sharePrice)
    ) {
      notification.warning({
        duration: 2,
        message: 'Form has error',
        placement: 'topRight',
        description:
          props.errors.investingProfile?.amount?.type === 'required'
            ? 'Amount is required.'
            : 'Amount must be in the correct format or value.',
        key: 'form-notif',
      })
      return
    } else {
      setIsLoading(true)

      const formattedUTMParams = getFormattedUTMParams()
      const pathname = location.pathname
      const currentParams = queryString.parse(location.search)
      const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'accredited_investor_type')

      try {
        consoleLog(props.getValues('investingProfile.amount'))
        if (typeof props.getValues('investingProfile.amount') === 'string') {
          props.setValue(
            'investingProfile.amount',
            Number(props.getValues('investingProfile.amount').replace(/[,]/g, '')),
          )
        } else if (typeof props.getValues('investingProfile.amount') === 'number') {
          props.setValue('investingProfile.amount', Number(props.getValues('investingProfile.amount')))
        }

        props.setValue(
          'investingProfile.totalShares',
          Number(props.getValues('investingProfile.amount')) / Number(sharePrice),
        )
        const data = {
          ...props.getValues(),
          lastCompletedStage: '6',
        }

        const updateOnboardProgressRes = await updateOnboardProgress(data)
        console.log('UPODAT_RESPONSE', updateOnboardProgressRes)
        if (updateOnboardProgressRes.status === 201 || updateOnboardProgressRes.status === 200) {
          sendDataToGTM({
            event: 'funnel_step',
            step_title: 'INVESTMENT_AMOUNT',
            ...gtmData,
            ...formattedUTMParams,
          })
          navigate(updatedUrl)
          props.nextStep('6')
        }
      } catch (error: any) {
        ReactGA.event({
          category: 'Investment amount page',
          action: `Investment amount page error`,
          label: JSON.stringify(error),
        })
        // catch the duplicate errors
        if (error.response.data === RiseUpErrors.DuplicatePhoneNumber) {
          setErrorMessage(RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.')
          return
        }
        if (error.response.data === RiseUpErrors.DuplicateSSN) {
          setErrorMessage(RiseUpErrors.DuplicateSSN + ' Please update the ssn.')
          return
        }
        swal({
          title: 'Error!',
          text: 'Please check if your details are valid and correct. If error persists, contact us.',
          icon: 'error',
        })
      }
    }
  }

  // useEffect(() => {
  //   sendDataToGTM({
  //     event: 'funnel_step',
  //     step_title: 'INVESTMENT_AMOUNT',
  //     ...gtmData,
  //   })
  // }, [])

  useEffect(() => {
    const amount = props.getValues('investingProfile.amount') ?? 0
    const totalShares = props.getValues('investingProfile.totalShares') ?? 0
    props.setValue('investingProfile.amount', amount)
    props.setValue('investingProfile.totalShares', totalShares)
  }, []) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side5} alt={`${Side5}`} />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        {/* <div className="justify-center mb-10 sm:hidden lg:flex">
          <div className="w-1/2">
            <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
          </div>
        </div> */}

        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
          {props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY
            ? 'How much is the company investing?'
            : props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT'
              ? 'How much is the trust investing?'
              : props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
                  props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT
                ? 'How much is the retirement account investing?'
                : 'How much are you investing?'}
        </div>

        <div>
          <FormLabel labelName={'Investment amount'} />
          <InputField
            registerField={props.registerField}
            fieldId={'investingProfile.amount'}
            validate={{
              numbersOnly: (value: any) => /^[0-9.,]+$/.test(value),
              minimumAmount: (value: any) => Number(String(value).replace(/[,]/g, '')) >= minInvestment,
              isDivisible: (value: any) => isAmtDivisibleBySharePrice(value, sharePrice),
              maximumAmount: (value: any) => Number(String(value).replace(/[,]/g, '')) <= maxInvestment,
            }}
            maskedInput={'numeric'}
            control={props.control}
            thousandsGroupStyle={'thousand'}
            thousandSeparator={true}
            decimalScale={2}
            value={String(props.getValues('investingProfile.amount'))}
            maskType={'numeric'}
            placeholder={`$ ${formatNumber(Number(sharePrice))}`}
            onChange={(e: any) => {
              props.clearErrors('investingProfile.amount')
              if (e && e.floatValue > 0) {
                props.setValue('investingProfile.totalShares', e.floatValue / sharePrice)
              } else {
                props.setValue('investingProfile.totalShares', 0)
              }
            }}
          />
          {props.errors.investingProfile?.amount?.type === 'numbersOnly' && (
            <ErrorMessage message={'Must input a valid amount.'} />
          )}
          {props.errors.investingProfile?.amount?.type === 'minimumAmount' && (
            <ErrorMessage message={`Minimum investment is $${minInvestment}.`} />
          )}
          {props.errors.investingProfile?.amount?.type === 'isDivisible' && (
            <ErrorMessage
              message={`Your investment amount must be divisible by $${formatNumber(sharePrice)}, the price per share.`}
            />
          )}
          {props.errors.investingProfile?.amount?.type === 'maximumAmount' && (
            <ErrorMessage message={`Maximum investment is $${maxInvestment}.`} />
          )}
          {props.errors.investingProfile?.amount?.type === 'required' && (
            <ErrorMessage message={`Amount is required.`} />
          )}
        </div>

        <div>
          <FormLabel labelName={'Total shares'} />
          <InputField
            registerField={props.registerField}
            fieldId={'investingProfile.totalShares'}
            validate={{
              numbersOnly: (value: any) => /^[0-9.,]+$/.test(value),
            }}
            maskedInput={'numeric'}
            control={props.control}
            thousandsGroupStyle={'thousand'}
            thousandSeparator={true}
            decimalScale={2}
            value={String(props.getValues('investingProfile.totalShares'))}
            maskType={'numeric'}
            placeholder={`0`}
            onChange={(e: any) => {
              if (e.floatValue) {
                props.setValue('investingProfile.amount', e.floatValue * sharePrice)
              } else {
                props.setValue('investingProfile.amount', '0')
              }
            }}
          />
          {props.errors.investingProfile?.totalShares?.type === 'numbersOnly' && (
            <ErrorMessage message={'Must input a valid amount for shares.'} />
          )}
          {props.errors.investingProfile?.totalShares?.type === 'required' && (
            <ErrorMessage message={`Total shares is required.`} />
          )}
        </div>

        <div className="mt-4 text-base leading-5 text-gray-500">
          Per share price is ${formatNumber(sharePrice)}. The amount you invest must be divisible by $
          {formatNumber(sharePrice)} and must be at least ${formatNumber(minInvestment)}
        </div>

        <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
          <div className="flex flex-col sm:w-[100%] lg:w-[20%]">
            <Button btnName={'Back'} onClickHandler={() => props.previousStep('4')} />
          </div>
          <div className="flex w-[100%] flex-col sm:text-sm">
            <Button
              btnName={'Next: Accreditation Info'}
              btnType={'button'}
              disabled={isSubmitDisabled()}
              onClickHandler={handleSubmit}
            />
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <MessageModal
        isVisible={errorMessage !== ''}
        onProceed={() => {
          // automatically set the step to appropriate form duplicate error
          if (errorMessage === RiseUpErrors.DuplicatePhoneNumber + ' Please update the phone number.') {
            setErrorMessage('')
            props.nextStep('3')
          }
          if (errorMessage === RiseUpErrors.DuplicateSSN + ' Please update the ssn.') {
            setErrorMessage('')
            props.nextStep('4')
          }
          setErrorMessage('')
        }}
        onCancel={() => {
          setErrorMessage('')
        }}
        title={'Error'}
        description={errorMessage}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default InvestmentAmount
